import { CENTS_IN_COIN_RATE, KWD_RATE } from "consts";
import { getFullName } from "helpers/dataHelpers";

import { GetOrdersResponseDto, OrderResponseDto } from "types/orders";
import {
  CustomerBalanceResponseDto,
  GetCustomersMappedResponseDto,
  GetCustomersResponseDto,
} from "types/customers";
import {
  CreateCustomerTransactionRequestDto,
  GetTransactionsResponseDto,
} from "types/transactions";
import { SetSettingsRequestDto, SettingsResponseDto } from "types/settings";
import {
  CreateOrUpdatePackageRequestDto,
  CreateOrUpdatePackageRulesRequestDto,
  PackageResponseDto,
  PackageRulesResponseDto,
  TrialPackageResponseDto,
} from "types/packages";
import { GetUsersResponseDto } from "types/users";

export const ordersMapper = (response: GetOrdersResponseDto) => {
  const { items, ...restResponse } = response;

  return {
    ...restResponse,
    items: items.map(item => ({
      ...item,
      totalFmcCentsPrice: item.totalFmcCentsPrice / CENTS_IN_COIN_RATE,
      totalFiatCentsPrice: item.totalFiatCentsPrice / CENTS_IN_COIN_RATE,
    })),
  };
};

export const customersMapper = (
  response: GetCustomersResponseDto,
): GetCustomersMappedResponseDto => {
  const { items, ...restResponse } = response;

  return {
    ...restResponse,
    items: items.map(profile => {
      const { firstName, lastName } = profile;

      const fullName = getFullName({ firstName, lastName });

      return {
        ...profile,
        fullName,
      };
    }),
  };
};

export const balanceMapper = (balance: CustomerBalanceResponseDto) => {
  return {
    ...balance,
    fmcCentsAmount: balance.fmcCentsAmount / CENTS_IN_COIN_RATE,
  };
};

export const transactionsMapper = (response: GetTransactionsResponseDto) => {
  const { items, ...restResponse } = response;

  return {
    ...restResponse,
    items: items.map(item => ({
      ...item,
      fmcCentsAmount: item.fmcCentsAmount / CENTS_IN_COIN_RATE,
      payment: {
        ...item.payment,
        amount: item.payment?.amount / KWD_RATE || null,
      },
    })),
  };
};

export const topUpMapper = ({
  fmcCentsAmount,
  ...restData
}: CreateCustomerTransactionRequestDto) => ({
  ...restData,
  fmcCentsAmount: fmcCentsAmount * CENTS_IN_COIN_RATE,
});

export const orderMapper = (order: OrderResponseDto) => {
  const { totalFiatCentsPrice, totalFmcCentsPrice, items, ...restOrder } =
    order;

  return {
    ...restOrder,
    totalFiatCentsPrice: totalFiatCentsPrice / CENTS_IN_COIN_RATE,
    totalFmcCentsPrice: totalFmcCentsPrice / CENTS_IN_COIN_RATE,
    items: items.map(item => ({
      ...item,
      fmcCentsPrice: item.fmcCentsPrice / CENTS_IN_COIN_RATE,
    })),
  };
};

export const minValuesMapper = (data: SettingsResponseDto) => {
  const { minimumOrderValue, minimumTopUpValue, ...restData } = data;

  return {
    ...restData,
    minimumOrderValue: minimumOrderValue / CENTS_IN_COIN_RATE,
    minimumTopUpValue: minimumTopUpValue / CENTS_IN_COIN_RATE,
  };
};

export const minValuesPayloadMapper = (data: SetSettingsRequestDto) => {
  const { minimumOrderValue, minimumTopUpValue, ...restData } = data;

  return {
    ...restData,
    minimumOrderValue: minimumOrderValue * CENTS_IN_COIN_RATE,
    minimumTopUpValue: minimumTopUpValue * CENTS_IN_COIN_RATE,
  };
};

export const trialPackageMapper = (
  responseData: TrialPackageResponseDto,
): TrialPackageResponseDto => {
  return {
    ...responseData,
    price: responseData.price / CENTS_IN_COIN_RATE,
  };
};

export const trialPackagePayloadMapper = (requestData: {
  active: boolean;
  price: number;
}): {
  active: boolean;
  price: number;
} => {
  return {
    ...requestData,
    price: requestData.price * CENTS_IN_COIN_RATE,
  };
};

export const CoinPackageRulesMapper = (
  responseData: PackageRulesResponseDto[],
): PackageRulesResponseDto[] => {
  return responseData.map((data: PackageRulesResponseDto) => ({
    ...data,
    from: data?.from / CENTS_IN_COIN_RATE,
    to: data?.to / CENTS_IN_COIN_RATE,
  }));
};

export const CoinPackagesMapper = (
  responseData: PackageResponseDto[],
): PackageResponseDto[] => {
  return responseData.map((data: PackageResponseDto) => ({
    ...data,
    price: data.price / CENTS_IN_COIN_RATE,
    coinsAmount: data.coinsAmount / CENTS_IN_COIN_RATE,
  }));
};

export const CoinPackagesPayloadMapper = (
  requestData: CreateOrUpdatePackageRequestDto,
): CreateOrUpdatePackageRequestDto => {
  return {
    ...requestData,
    price: requestData.price * CENTS_IN_COIN_RATE,
  };
};

export const PackageRulesPayloadMapper = (
  requestData: CreateOrUpdatePackageRulesRequestDto,
): CreateOrUpdatePackageRulesRequestDto => {
  return {
    ...requestData,
    from: requestData.from * CENTS_IN_COIN_RATE,
    to: requestData.to * CENTS_IN_COIN_RATE,
  };
};

export const usersMapper = (
  response: GetUsersResponseDto,
): GetUsersResponseDto => {
  const { items, ...restResponse } = response;

  return {
    ...restResponse,
    items: items.map(profile => {
      const { firstName, lastName } = profile;

      const fullName = getFullName({ firstName, lastName });

      return {
        ...profile,
        fullName,
      };
    }),
  };
};
